import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Calendar, Plus, Trash2, Save, AlertCircle, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { availabilityAPI } from '../../services/api';
import { toast } from 'sonner';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday', 
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday'
};

const AvailabilityTab = ({ previewMode = false }) => {
  const [availability, setAvailability] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showExceptionModal, setShowExceptionModal] = useState(false);
  const [newException, setNewException] = useState({ date: '', is_available: false, reason: '' });

  useEffect(() => {
    if (!previewMode) {
      fetchAvailability();
    } else {
      // Preview mode sample data
      setAvailability(DAYS.map(day => ({
        day,
        is_available: !['saturday', 'sunday'].includes(day),
        time_slots: !['saturday', 'sunday'].includes(day) 
          ? [{ start_time: '09:00', end_time: '17:00' }] 
          : []
      })));
      setLoading(false);
    }
  }, [previewMode]);

  const fetchAvailability = async () => {
    try {
      const [availRes, exceptionsRes] = await Promise.all([
        availabilityAPI.getMy(),
        availabilityAPI.getMyExceptions({})
      ]);
      
      if (availRes.data.availability) {
        setAvailability(availRes.data.availability);
      } else {
        // Initialize default
        setAvailability(DAYS.map(day => ({
          day,
          is_available: !['saturday', 'sunday'].includes(day),
          time_slots: !['saturday', 'sunday'].includes(day) 
            ? [{ start_time: '09:00', end_time: '17:00' }] 
            : []
        })));
      }
      setExceptions(exceptionsRes.data || []);
    } catch (error) {
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (day) => {
    setAvailability(prev => prev.map(d => {
      if (d.day === day) {
        const newAvailable = !d.is_available;
        return {
          ...d,
          is_available: newAvailable,
          time_slots: newAvailable && d.time_slots.length === 0 
            ? [{ start_time: '09:00', end_time: '17:00' }]
            : d.time_slots
        };
      }
      return d;
    }));
  };

  const updateTimeSlot = (day, slotIndex, field, value) => {
    setAvailability(prev => prev.map(d => {
      if (d.day === day) {
        const newSlots = [...d.time_slots];
        newSlots[slotIndex] = { ...newSlots[slotIndex], [field]: value };
        return { ...d, time_slots: newSlots };
      }
      return d;
    }));
  };

  const addTimeSlot = (day) => {
    setAvailability(prev => prev.map(d => {
      if (d.day === day) {
        return {
          ...d,
          time_slots: [...d.time_slots, { start_time: '09:00', end_time: '17:00' }]
        };
      }
      return d;
    }));
  };

  const removeTimeSlot = (day, slotIndex) => {
    setAvailability(prev => prev.map(d => {
      if (d.day === day) {
        return {
          ...d,
          time_slots: d.time_slots.filter((_, i) => i !== slotIndex)
        };
      }
      return d;
    }));
  };

  const saveAvailability = async () => {
    if (previewMode) {
      toast.info('Cannot save in preview mode');
      return;
    }
    
    setSaving(true);
    try {
      await availabilityAPI.updateMy({ availability, timezone: 'America/New_York' });
      toast.success('Availability saved successfully');
    } catch (error) {
      toast.error('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const addException = async () => {
    if (previewMode) {
      toast.info('Cannot add exceptions in preview mode');
      return;
    }
    
    if (!newException.date) {
      toast.error('Please select a date');
      return;
    }
    
    try {
      await availabilityAPI.addException(newException);
      toast.success('Exception added');
      setShowExceptionModal(false);
      setNewException({ date: '', is_available: false, reason: '' });
      fetchAvailability();
    } catch (error) {
      toast.error('Failed to add exception');
    }
  };

  const removeException = async (date) => {
    if (previewMode) return;
    
    try {
      await availabilityAPI.removeException(date);
      toast.success('Exception removed');
      setExceptions(prev => prev.filter(e => e.date !== date));
    } catch (error) {
      toast.error('Failed to remove exception');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[var(--primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">My Availability</h2>
          <p className="text-[var(--text-muted)] text-sm">Set your weekly schedule and time off</p>
        </div>
        <Button onClick={saveAvailability} disabled={saving || previewMode}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Weekly Schedule */}
      <div className="card p-6">
        <h3 className="font-bold mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" /> Weekly Schedule
        </h3>
        
        <div className="space-y-4">
          {availability.map(dayAvail => (
            <div key={dayAvail.day} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleDay(dayAvail.day)}
                    className={`w-10 h-6 rounded-full transition-all ${
                      dayAvail.is_available ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-[var(--background-paper)] shadow transition-all ${
                      dayAvail.is_available ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </button>
                  <span className="font-medium">{DAY_LABELS[dayAvail.day]}</span>
                </div>
                
                {dayAvail.is_available && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addTimeSlot(dayAvail.day)}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Time Slot
                  </Button>
                )}
              </div>
              
              {dayAvail.is_available ? (
                <div className="space-y-2 ml-13">
                  {dayAvail.time_slots.map((slot, idx) => (
                    <div key={`${dayAvail.day}-slot-${idx}`} className="flex items-center gap-3">
                      <Clock className="w-4 h-4 text-[var(--text-muted)]" />
                      <Input
                        type="time"
                        value={slot.start_time}
                        onChange={(e) => updateTimeSlot(dayAvail.day, idx, 'start_time', e.target.value)}
                        className="w-32"
                      />
                      <span className="text-[var(--text-muted)]">to</span>
                      <Input
                        type="time"
                        value={slot.end_time}
                        onChange={(e) => updateTimeSlot(dayAvail.day, idx, 'end_time', e.target.value)}
                        className="w-32"
                      />
                      {dayAvail.time_slots.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTimeSlot(dayAvail.day, idx)}
                          className="text-[var(--error)] hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[var(--text-muted)] text-sm ml-13">Not available</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Time Off / Exceptions */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold flex items-center gap-2">
            <AlertCircle className="w-5 h-5" /> Time Off & Exceptions
          </h3>
          <Button variant="outline" size="sm" onClick={() => setShowExceptionModal(true)}>
            <Plus className="w-4 h-4 mr-1" /> Add Time Off
          </Button>
        </div>
        
        {exceptions.length === 0 ? (
          <p className="text-[var(--text-muted)] text-center py-4">No scheduled time off</p>
        ) : (
          <div className="space-y-2">
            {exceptions.map(exc => (
              <div key={exc.date} className="flex items-center justify-between p-3 bg-[var(--background-subtle)] rounded-lg">
                <div>
                  <p className="font-medium">{exc.date}</p>
                  <p className="text-sm text-[var(--text-muted)]">
                    {exc.is_available ? 'Partial availability' : 'Day off'}
                    {exc.reason && ` - ${exc.reason}`}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeException(exc.date)}
                  className="text-[var(--error)]"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Exception Modal */}
      {showExceptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-xl max-w-md w-full p-6"
          >
            <h3 className="text-lg font-bold mb-4">Add Time Off</h3>
            
            <div className="space-y-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newException.date}
                  onChange={(e) => setNewException(prev => ({ ...prev, date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div>
                <Label>Reason (optional)</Label>
                <Input
                  value={newException.reason}
                  onChange={(e) => setNewException(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="e.g., Doctor's appointment, Vacation"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="partial"
                  checked={newException.is_available}
                  onChange={(e) => setNewException(prev => ({ ...prev, is_available: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="partial">Partially available (some hours)</Label>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowExceptionModal(false)}
              >
                Cancel
              </Button>
              <Button className="flex-1" onClick={addException}>
                Add Time Off
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default AvailabilityTab;
